import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, RefreshCw, Wrench } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { Panel } from '@/components/aperturas/shared';
import EstadoBadge from '@/components/dispositivos/EstadoBadge';
import { CASO_LABEL, fechaHora, MARCA_LABEL, TIPO_LABEL, type AuditoriaDispositivos, type Discrepancia, type InformeMigracion, type ResumenDispositivo, type ResultadoSincronizacion } from '@/lib/dispositivos';

/**
 * Dónde no cuadran las tres fuentes: las cuentas de Nuki y SwitchBot, los
 * custom fields de Hostify y el inventario. Cada bloque lleva su acción:
 * sincronizar, adoptar lo que dice Hostify, escribir lo que dice el
 * inventario, reintentar, o ir a la ficha a completar lo que falta.
 */
export default function DispositivosAuditoriaPage() {
  const { data, loading, error, refresh } = useApi<AuditoriaDispositivos>(() => api.getDispositivosAuditoria(true), []);
  const [sincronizando, setSincronizando] = useState(false);
  const [resultadoSync, setResultadoSync] = useState<ResultadoSincronizacion | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      setResultadoSync(await api.sincronizarDispositivos());
      refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido sincronizar');
    } finally {
      setSincronizando(false);
    }
  };

  const accion = async (clave: string, fn: () => Promise<unknown>) => {
    setOcupado(clave);
    try {
      const r = (await fn()) as { fallos?: string[] } | undefined;
      if (r?.fallos && r.fallos.length > 0) alert(r.fallos.join('\n'));
      refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido hacer');
    } finally {
      setOcupado(null);
    }
  };

  if (loading && !data) return <LoadingSpinner />;
  if (error || !data) return <EmptyState title="No se ha podido cargar la auditoría" description={error ?? undefined} />;

  const { local, cuentas } = data;
  const total =
    local.discrepancias +
    local.hostifyVsInventario.filter((x) => x.caso === 'pendiente').length +
    local.incompletos.length +
    (cuentas ? cuentas.enCuentaSinFicha.length + cuentas.customFieldsRotos.length : 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-sereno-dark/50">
          {total === 0 ? 'Todo cuadra.' : `${total} cosa${total === 1 ? '' : 's'} que no cuadra${total === 1 ? '' : 'n'}.`} Se compara lo que dicen las cuentas, Hostify (copia de las 05:00) y el inventario.
        </p>
        <button onClick={() => void sincronizar()} disabled={sincronizando} className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-50">
          <RefreshCw size={16} className={sincronizando ? 'animate-spin' : ''} />
          {sincronizando ? 'Sincronizando…' : 'Sincronizar con Nuki y SwitchBot'}
        </button>
      </div>

      {resultadoSync && (
        <div className="rounded-lg border border-sereno-blue bg-sereno-blue/20 p-3 text-sm">
          Sincronizado: {resultadoSync.contadores.creados} nuevos, {resultadoSync.contadores.actualizados} refrescados, {resultadoSync.contadores.instalacionesDeducidas} instalaciones deducidas
          {resultadoSync.contadores.bridgesDeducidos ? `, ${resultadoSync.contadores.bridgesDeducidos} bridges deducidos` : ''}
          {resultadoSync.limpieza.accesoriosBorrados > 0 ? `, ${resultadoSync.limpieza.accesoriosBorrados} accesorios que la cuenta ya no confirma borrados` : ''}.
          {resultadoSync.avisos.length > 0 && (
            <ul className="mt-1 text-xs text-amber-800 list-disc list-inside max-h-32 overflow-y-auto">
              {resultadoSync.avisos.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
        </div>
      )}

      {data.errorCuentas && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>{data.errorCuentas} Los bloques 1 y 3 no se han podido comprobar.</span>
        </div>
      )}

      <Bloque n={1} titulo="En la cuenta y sin ficha" hint="Lo que Nuki o SwitchBot listan y el inventario no conoce. «Sincronizar» les crea la ficha." vacio={!cuentas || cuentas.enCuentaSinFicha.length === 0} textoVacio={cuentas ? 'Todo lo de las cuentas tiene ficha.' : 'Sin comprobar.'}>
        <ul className="space-y-1 text-sm">
          {cuentas?.enCuentaSinFicha.map((s) => (
            <li key={`${s.marca}-${s.idFabricante}`} className="flex items-center gap-2">
              <span className="font-mono">{s.idFabricante}</span>
              <span className="text-slate-500">{MARCA_LABEL[s.marca]} · {TIPO_LABEL[s.tipo]}{s.alias ? ` · ${s.alias}` : ''}</span>
            </li>
          ))}
        </ul>
      </Bloque>

      <Bloque n={2} titulo="Hostify ≠ inventario" hint="Por casa y custom field: lo que dice Hostify frente a lo que dice el inventario." vacio={local.hostifyVsInventario.length === 0} textoVacio="Hostify y el inventario dicen lo mismo en todas las casas.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="text-left font-medium pb-2 pr-3">Casa</th>
                <th className="text-left font-medium pb-2 pr-3">Campo</th>
                <th className="text-left font-medium pb-2 pr-3">Hostify</th>
                <th className="text-left font-medium pb-2 pr-3">Inventario</th>
                <th className="text-left font-medium pb-2 pr-3">Qué pasa</th>
                <th className="text-right font-medium pb-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {local.hostifyVsInventario.map((x) => (
                <tr key={`${x.listingId}-${x.campo}`} className="border-t border-slate-100 align-top">
                  <td className="py-2 pr-3 whitespace-nowrap">{x.nickname}</td>
                  <td className="py-2 pr-3 text-slate-500">{x.campo}</td>
                  <td className="py-2 pr-3">
                    {x.hostify ? <span className="font-mono">{x.hostify}</span> : <span className="text-slate-300">vacío</span>}
                    {x.hostify && !x.fichaDeHostify && <span className="block text-[11px] text-amber-700">sin ficha en el inventario</span>}
                  </td>
                  <td className="py-2 pr-3"><Ficha d={x.inventario} /></td>
                  <td className="py-2 pr-3 text-slate-600 text-xs">{CASO_LABEL[x.caso]}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <AccionesDiscrepancia x={x} ocupado={ocupado} onAccion={accion} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Bloque>

      <Bloque n={3} titulo="Custom field roto" hint="Apunta a un id que no existe ni en la cuenta ni en el inventario: casi siempre un dígito mal. Se corrige en Hostify." vacio={!cuentas || cuentas.customFieldsRotos.length === 0} textoVacio={cuentas ? 'Ningún custom field apunta al vacío.' : 'Sin comprobar.'}>
        <ul className="space-y-1 text-sm">
          {cuentas?.customFieldsRotos.map((r) => (
            <li key={`${r.listingId}-${r.campo}`}>
              <span className="font-medium">{r.nickname}</span> · campo {r.campo} → <span className="font-mono">{r.valor}</span>
            </li>
          ))}
        </ul>
      </Bloque>

      <Bloque n={4} titulo="En stock pero habla" hint="Está en stock, cuarentena o reparación y ha dado señales de vida esta semana: alguien lo instaló sin apuntarlo. Instálalo desde su ficha." vacio={local.enStockPeroHabla.length === 0} textoVacio="Nada en stock da señales de vida.">
        <ul className="space-y-1 text-sm">
          {local.enStockPeroHabla.map((h) => (
            <li key={h.dispositivo.id} className="flex items-center gap-2 flex-wrap">
              <Ficha d={h.dispositivo} />
              <span className="text-slate-500 text-xs">{h.senal === 'eventos' ? 'eventos en Nuki' : 'online según la API'} · {fechaHora(h.cuando)}</span>
            </li>
          ))}
        </ul>
      </Bloque>

      <Bloque n={5} titulo="Cobertura" hint="Un opener SIEMPRE necesita bridge, y una smartlock solo si no tiene Wi-Fi propio; un bridge por casa, aunque tenga las dos cosas, y «Sincronizar» lo da de alta solo donde falte; un bot necesita su hub; un keypad, aquello a lo que está emparejado. Y toda casa activa necesita smartlock y keypad." vacio={local.cobertura.faltas.length === 0 && local.cobertura.casasIncompletas.length === 0 && !local.cobertura.bridgesSobrantes?.length} textoVacio="Todas las casas tienen lo que necesitan.">
        {local.cobertura.faltas.length > 0 && (
          <ul className="space-y-1 text-sm mb-3">
            {local.cobertura.faltas.map((f, i) => (
              <li key={i} className="flex items-center gap-2 flex-wrap">
                <Ficha d={f.dispositivo} />
                <span className="text-slate-600">en {f.nickname} necesita <span className="font-medium">{TIPO_LABEL[f.necesita]}</span></span>
                {f.detalle && <span className="text-xs text-amber-700">{f.detalle}</span>}
              </li>
            ))}
          </ul>
        )}
        {!!local.cobertura.bridgesSobrantes?.length && (
          <div className="mb-3">
            <p className="text-xs font-medium text-slate-500 mb-1">Bridges que sobran: en esa casa ya nada lo necesita (¿se cambió a una Pro con Wi-Fi?). Retíralo desde su ficha si ya no está.</p>
            <ul className="space-y-1 text-sm">
              {local.cobertura.bridgesSobrantes.map((b) => (
                <li key={`${b.dispositivo.id}-${b.listingId}`} className="flex items-center gap-2 flex-wrap">
                  <Ficha d={b.dispositivo} />
                  <span className="text-slate-600">en {b.nickname}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {local.cobertura.casasIncompletas.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-1">Casas activas incompletas</p>
            <ul className="space-y-1 text-sm">
              {local.cobertura.casasIncompletas.map((c) => (
                <li key={c.listingId}>
                  <Link to={`/dispositivos?listingId=${c.listingId}`} className="font-medium hover:underline">{c.nickname}</Link>
                  <span className="text-slate-500"> · sin {c.falta.map((t) => TIPO_LABEL[t].toLowerCase()).join(' ni ')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Bloque>

      <Bloque n={6} titulo="Sin modelo o sin serie" hint="El modelo decide si una smartlock necesita bridge; la serie identifica lo que no tiene API. Se completan en «Editar»." vacio={local.incompletos.length === 0} textoVacio="Todo tiene modelo y serie.">
        <ul className="space-y-1 text-sm">
          {local.incompletos.map((i) => (
            <li key={i.dispositivo.id} className="flex items-center gap-2">
              <Ficha d={i.dispositivo} />
              <span className="text-slate-500 text-xs">falta {i.falta.join(' y ')}</span>
            </li>
          ))}
        </ul>
      </Bloque>

      <PonerAlDia onHecho={refresh} />
    </div>
  );
}

/**
 * Poner al día lo ya guardado cuando cambia un criterio: fichas que siguen
 * diciendo «cerradura» o «stock usado», modelos que ahora se deducen y los
 * accesorios que la cuenta ya no confirma.
 *
 * Primero enseña qué cambiaría y solo escribe cuando se confirma. No borra
 * nada que haya tocado una persona: eso sale aparte para mirarlo.
 */
function PonerAlDia({ onHecho }: { onHecho: () => void }) {
  const [informe, setInforme] = useState<InformeMigracion | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  const lanzar = async (aplicar: boolean) => {
    setTrabajando(true);
    try {
      const r = await api.migrarDispositivos(aplicar);
      setInforme(r);
      if (aplicar) onHecho();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido poner al día');
    } finally {
      setTrabajando(false);
    }
  };

  const cambios = informe ? informe.actualizar.length + informe.borrar.length : 0;

  return (
    <Panel title="Poner al día el inventario" hint="Cuando cambia un criterio, las fichas ya guardadas se quedan con el anterior. Esto las repasa: primero enseña qué cambiaría.">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => void lanzar(false)} disabled={trabajando} className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-50">
          <Wrench size={15} />
          {trabajando ? 'Repasando…' : 'Ver qué cambiaría'}
        </button>
        {informe && !informe.aplicado && cambios > 0 && (
          <button onClick={() => void lanzar(true)} disabled={trabajando} className="btn-primary text-sm disabled:opacity-50">
            Aplicar {cambios} cambio{cambios === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {informe && (
        <div className="mt-3 space-y-2 text-sm">
          {informe.avisoCuenta && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{informe.avisoCuenta}</span>
            </p>
          )}

          {cambios === 0 ? (
            <p className="text-slate-400 flex items-center gap-1.5">
              <CheckCircle2 size={14} className="text-green-500" />
              Nada que poner al día: las {informe.sinCambios} fichas ya están con el criterio de ahora.
            </p>
          ) : (
            <p className="text-sereno-dark/70">
              {informe.aplicado ? 'Hecho: ' : 'Cambiaría '}
              {informe.actualizar.length} ficha{informe.actualizar.length === 1 ? '' : 's'} y {informe.borrar.length} sensor{informe.borrar.length === 1 ? '' : 'es'} de puerta que no existen.
              {' '}{informe.sinCambios} se queda{informe.sinCambios === 1 ? '' : 'n'} igual.
            </p>
          )}

          {informe.borrar.length > 0 && (
            <Lista titulo={informe.aplicado ? 'Accesorios borrados' : 'Accesorios que se borrarían'}>
              {informe.borrar.map((b) => (
                <li key={b.id}><span className="font-mono">{b.id}</span> <span className="text-slate-500">— {b.motivo}</span></li>
              ))}
            </Lista>
          )}

          {informe.actualizar.length > 0 && (
            <Lista titulo={informe.aplicado ? 'Fichas actualizadas' : 'Fichas que se actualizarían'}>
              {informe.actualizar.map((a) => (
                <li key={a.id}><span className="font-mono">{a.id}</span> <span className="text-slate-500">— {a.que.join(', ')}</span></li>
              ))}
            </Lista>
          )}

          {informe.revisar.length > 0 && (
            <Lista titulo="No se tocan: míralos tú">
              {informe.revisar.map((r, i) => <li key={i} className="text-amber-800">{r}</li>)}
            </Lista>
          )}

        </div>
      )}
    </Panel>
  );
}

function Lista({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <details className="rounded-lg border border-slate-200 p-2">
      <summary className="cursor-pointer text-xs font-medium text-slate-600">{titulo}</summary>
      <ul className="mt-1.5 space-y-0.5 text-xs max-h-64 overflow-y-auto list-disc list-inside">{children}</ul>
    </details>
  );
}

function Bloque({ n, titulo, hint, vacio, textoVacio, children }: { n: number; titulo: string; hint: string; vacio: boolean; textoVacio: string; children: React.ReactNode }) {
  return (
    <Panel title={`${n}. ${titulo}`} hint={hint}>
      {vacio ? (
        <p className="text-sm text-slate-400 flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-500" /> {textoVacio}</p>
      ) : (
        children
      )}
    </Panel>
  );
}

function Ficha({ d }: { d: ResumenDispositivo | null }) {
  if (!d) return <span className="text-slate-300">nada</span>;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Link to={`/dispositivos/${d.id}`} className="font-mono hover:underline">{d.idFabricante}</Link>
      <span className="text-slate-400 text-xs">{TIPO_LABEL[d.tipo]}{d.alias ? ` · ${d.alias}` : ''}</span>
      <EstadoBadge estado={d.estado} pequeno />
    </span>
  );
}

function AccionesDiscrepancia({ x, ocupado, onAccion }: { x: Discrepancia; ocupado: string | null; onAccion: (clave: string, fn: () => Promise<unknown>) => Promise<void> }) {
  const clave = `${x.listingId}-${x.campo}`;
  const trabajando = ocupado === clave;
  const botones: React.ReactNode[] = [];

  if ((x.caso === 'hostify_sin_inventario' || x.caso === 'distintos') && x.fichaDeHostify) {
    const ficha = x.fichaDeHostify;
    botones.push(
      <button key="adoptar" disabled={trabajando} onClick={() => void onAccion(clave, () => api.adoptarDeHostify({ listingId: x.listingId, dispositivoId: ficha.id, campo: x.campo }))} className="btn-secondary text-xs disabled:opacity-50" title="El inventario pasa a decir lo que dice Hostify">
        Adoptar Hostify
      </button>,
    );
  }
  if ((x.caso === 'inventario_sin_hostify' || x.caso === 'distintos') && x.inventario) {
    const inv = x.inventario;
    botones.push(
      <button key="escribir" disabled={trabajando} onClick={() => void onAccion(clave, () => api.escribirHostifyDispositivo(inv.id, x.listingId))} className="btn-secondary text-xs disabled:opacity-50" title="Hostify pasa a decir lo que dice el inventario">
        Escribir Hostify
      </button>,
    );
  }
  if (x.caso === 'pendiente' && x.inventario) {
    const inv = x.inventario;
    botones.push(
      <button key="reintentar" disabled={trabajando} onClick={() => void onAccion(clave, () => api.reintentarHostifyDispositivo(inv.id))} className="btn-secondary text-xs disabled:opacity-50">
        Reintentar
      </button>,
    );
  }
  if (botones.length === 0) return <span className="text-xs text-slate-400">sincroniza primero</span>;
  return <span className="inline-flex gap-1">{botones}</span>;
}
